import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { setUserId, trackEventAsync } from 'sh-www/utils/datalayer';
import { action } from '@ember/object';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { task } from 'ember-concurrency';
import { USER } from 'sh-www/models/user';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { blockingLocationAssign } from 'sh-www/utils/window';

const UNKNOWN_ERROR_MESSAGE = 'An unknown error occurred.';

class JoinCompanyController extends Controller {
  @service auth;
  @service router;
  @service session;
  @service store;
  @service('commands/user-commands') userCommandService;

  /** @inheritDoc */
  queryParams = ['id']; // 'id' == invite code

  /** @type {string} */
  get companyLogoSrcSet() {
    const profile128 = this.model.company.image.transforms.profile128;
    const profile480 = this.model.company.image.transforms.profile480;
    return `${profile128} 1x, ${profile480} 2x`;
  }

  get errorMessage() {
    const errors = this.model.errors;
    return errors ? errors['generic-exception']?.[0] : UNKNOWN_ERROR_MESSAGE;
  }

  get errorMessageDeservesPasswordReset() {
    const { errorMessage } = this;
    return errorMessage === 'Invite is no longer valid'
      || errorMessage === 'Invite already accepted';
  }

  get errorMessageDetails() {
    if (this.errorMessage === 'Invite is expired') {
      return 'Ask the account owner to resend your invite';
    } else if (this.errorMessageDeservesPasswordReset) {
      return 'Reset your password if you lost access to your account';
    }

    return '';
  }

  /** @type {string} */
  get fullName() {
    return `${this.model.firstName || ''} ${this.model.lastName || ''}`.trim();
  }

  @action
  redirectPasswordReset() {
    this.router.transitionTo(EMBER_ROUTE_NAMES.RESET_PASSWORD);
  }

  /**
   * @param {Object} param
   * @param {string} param.full_name
   * @param {string} param.email
   * @param {string} param.password
   * @param {boolean} param.legal_accept
   * @returns {Promise<any>}
   */
  @task
  *completeSignup({ full_name, email, password }) {
    const user = this.model;
    const userParams = { full_name, legal_accept: true };

    yield this.userCommandService.updateUser(user, userParams);

    if (!user.hasPassword) {
      yield this.auth.updatePassword(email, password, password);
    }

    setUserId(this, user.id);

    yield trackEventAsync(this, 'sign_up');
    yield blockingLocationAssign('/start');
  }

  @task
  *onGoogleSSO(credential) {
    try {
      if (!this.session.isAuthenticated) {
        yield this.auth.signupGoogleJwt(credential, USER.TYPES.SELLER);
      } else {
        yield this.auth.loginGoogleJwt(credential);
      }
    } catch (e) {
      this.errorTracker.warn('Google authentication failed', this, e);
      handleErrorResponseWithModal(this, e);
      throw e;
    }

    setUserId(this, this.session.currentUser.id);

    yield trackEventAsync(this, 'sign_up');
    yield blockingLocationAssign('/start');
  }
}

export default JoinCompanyController;
